import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import firebase from 'firebase/compat';
import { BehaviorSubject, finalize, Observable, Subscription } from 'rxjs';

import { DeviceToken, NotificationModel, NotificationsInfo, PaginatedList } from '@shared/models';

import { DeviceInfoService } from './device-info.service';
import { LogsService } from './http-client/logs.service';
import { NotificationHttpService } from './http-client/notification-http.service';
import { ToastsService } from './toasts.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  notificationsInfo$: BehaviorSubject<NotificationsInfo> = new BehaviorSubject<NotificationsInfo>({
    noOfNewNotifications: 0,
  });

  lastNotification$: BehaviorSubject<firebase.messaging.MessagePayload> =
    new BehaviorSubject<firebase.messaging.MessagePayload>(null);

  private messagesSub?: Subscription;
  private tokenSub?: Subscription;
  private deviceSub?: Subscription;

  constructor(
    private afMessaging: AngularFireMessaging,
    private messageService: ToastsService,
    private maintenanceService: LogsService,
    private notificationHttpService: NotificationHttpService,
    private deviceInfoService: DeviceInfoService
  ) {}

  get(loader: string, pageIndex = 0, pageSize = 50, filter = 'All'): Observable<PaginatedList<NotificationModel>> {
    return this.notificationHttpService.get(loader, pageIndex, pageSize, filter);
  }

  requestPermission() {
    if (!('Notification' in window) || Notification.permission === 'denied') {
      return;
    }

    this.tokenSub?.unsubscribe();
    this.tokenSub = this.afMessaging.requestToken.subscribe({
      next: (token) => {
        const result = new DeviceToken(
          this.deviceInfoService.getDeviceUuid(),
          token,
          this.deviceInfoService.getDeviceDescription()
        );
        this.activateDevice(result);
      },
      error: (error) => {
        const message = !error.message && !error.stack ? error.toString() : error.message;
        this.maintenanceService.saveWebExceptionLog(message, error.stack).subscribe(() => {});
        this.messageService.showError(error);
      },
    });
  }

  stopListening() {
    this.tokenSub?.unsubscribe();
    this.tokenSub = undefined;
    this.deviceSub?.unsubscribe();
    this.deviceSub = undefined;
    this.messagesSub?.unsubscribe();
    this.messagesSub = undefined;
  }

  private listen() {
    if (this.messagesSub) {
      return;
    }

    this.messagesSub = this.afMessaging.messages.subscribe((payload) => {
      this.lastNotification$.next(payload);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((payload?.notification?.body || payload?.notification?.title) && !(payload?.data as any)?.IsHidden) {
        this.messageService.showInfo(payload.notification.body, payload.notification.title);
        this.refreshNotificationsInfo();
      }
    });
  }

  private activateDevice(deviceinfo: DeviceToken) {
    if (deviceinfo) {
      this.deviceSub?.unsubscribe();
      this.deviceSub = this.notificationHttpService.updateDeviceInfo('', deviceinfo).subscribe({
        next: () => {
          this.listen();
        },
        error: (error) => this.messageService.showError(error),
      });
    }
  }

  markAllAsRead(loader: string): Observable<void> {
    return this.notificationHttpService
      .markAllAsRead(loader)
      .pipe(finalize(async () => await this.refreshNotificationsInfo()));
  }

  async refreshNotificationsInfo() {
    try {
      let notificationsInfo = await this.notificationHttpService.getNotificationsInfo('').toPromise();
      if (!notificationsInfo) {
        notificationsInfo = {
          noOfNewNotifications: 0,
        };
      }
      this.notificationsInfo$.next(notificationsInfo);
    } catch {
      //ignore exception - is not crucial for functionality
    }
  }
}
